import { useState, useEffect, useCallback } from "react";
import { Loader2, Upload, FolderOpen, FolderPlus, FileText, FileBox, Trash2, Download, RotateCcw, ChevronRight, Home } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";

const opts = { withCredentials: true };

const ESTADO_BADGE = {
  vigente: "bg-emerald-100 text-emerald-700",
  vencido: "bg-amber-100 text-amber-700 line-through",
  original: "bg-blue-100 text-blue-700",
};

function FileIcon({ filename }) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ext === "dwg" || ext === "dxf") {
    return <FileBox size={16} className="text-amber-500 flex-shrink-0" />;
  }
  return <FileText size={16} className="text-gray-400 flex-shrink-0" />;
}

export default function ArchivosTab({ obraId, canFullAccess }) {
  const [carpetas, setCarpetas] = useState([]);
  const [archivos, setArchivos] = useState({});
  const [activa, setActiva] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showPapelera, setShowPapelera] = useState(false);
  const [papelera, setPapelera] = useState([]);

  const [subcarpetas, setSubcarpetas] = useState([]);
  const [path, setPath] = useState([]); // [{id, nombre}]
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [reemplazaId, setReemplazaId] = useState("");

  const currentFolderId = path.length ? path[path.length - 1].id : null;

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/obras/${obraId}/archivos`, opts)
      .then(({ data }) => { setCarpetas(data.carpetas || []); setArchivos(data.archivos || {}); if (!activa) setActiva((data.carpetas || [])[0] || ""); })
      .catch(() => toast.error("Error cargando archivos"))
      .finally(() => setLoading(false));
  }, [obraId, activa]);
  useEffect(load, [load]);

  const loadSubcarpetas = useCallback(() => {
    if (!activa) return;
    axios.get(`${API}/obras/${obraId}/archivos/carpetas`, { ...opts, params: { carpeta_base: activa } })
      .then(({ data }) => setSubcarpetas(data || []))
      .catch(() => {});
  }, [obraId, activa]);
  useEffect(loadSubcarpetas, [loadSubcarpetas]);

  const cambiarCarpeta = (c) => { setActiva(c); setPath([]); setReemplazaId(""); };
  const entrarCarpeta = (folder) => { setPath([...path, { id: folder.id, nombre: folder.nombre }]); setReemplazaId(""); };
  const irABreadcrumb = (idx) => { setPath(idx < 0 ? [] : path.slice(0, idx + 1)); setReemplazaId(""); };

  const crearCarpeta = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/obras/${obraId}/archivos/carpetas`, {
        carpeta_base: activa, parent_id: currentFolderId, nombre: newFolderName.trim(),
      }, opts);
      toast.success("Carpeta creada");
      setNewFolderName(""); setShowNewFolder(false);
      loadSubcarpetas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear carpeta"); }
  };

  const eliminarCarpeta = async (id) => {
    try {
      await axios.delete(`${API}/obras/${obraId}/archivos/carpetas/${id}`, opts);
      toast.success("Carpeta eliminada");
      loadSubcarpetas();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const upload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("carpeta", activa);
      if (currentFolderId) fd.append("subcarpeta_id", currentFolderId);
      if (activa === "Planos") {
        fd.append("estado_plano", reemplazaId ? "vigente" : (archivosVista.length === 0 ? "original" : "vigente"));
        if (reemplazaId) fd.append("reemplaza_a_id", reemplazaId);
      }
      Array.from(files).forEach(f => fd.append("files", f));
      await axios.post(`${API}/obras/${obraId}/archivos/upload`, fd, opts);
      toast.success("Archivo(s) subido(s)");
      setReemplazaId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const download = (id) => window.open(`${API}/obras/${obraId}/archivos/${id}/download`, "_blank");

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API}/obras/${obraId}/archivos/${id}`, opts);
      toast.success("Enviado a la papelera");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cambiarEstadoPlano = async (id, estado_plano) => {
    try {
      const fd = new FormData();
      fd.append("estado_plano", estado_plano);
      await axios.put(`${API}/obras/${obraId}/archivos/${id}/estado-plano`, fd, opts);
      toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const abrirPapelera = async () => {
    setShowPapelera(true);
    try {
      const { data } = await axios.get(`${API}/obras/${obraId}/archivos/papelera`, opts);
      setPapelera(data.archivos || []);
    } catch { toast.error("Error cargando papelera"); }
  };

  const restaurar = async (id) => {
    try {
      await axios.post(`${API}/obras/${obraId}/archivos/${id}/restore`, {}, opts);
      toast.success("Archivo restaurado");
      abrirPapelera(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="grid grid-cols-4 gap-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>;

  const todosLosArchivos = archivos[activa] || [];
  const carpetasHijas = subcarpetas.filter(s => (s.parent_id || null) === currentFolderId);
  const archivosVista = todosLosArchivos.filter(a => (a.subcarpeta_id || null) === currentFolderId);

  return (
    <div className="space-y-4" data-testid="obra-archivos-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5 flex-wrap">
          {carpetas.map(c => (
            <button key={c} onClick={() => cambiarCarpeta(c)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${activa === c ? "bg-[#EA580C] text-white border-[#EA580C]" : "bg-white text-gray-600 border-gray-200"}`} data-testid={`archivos-carpeta-${c}`}>
              {c} ({(archivos[c] || []).length})
            </button>
          ))}
        </div>
        <button onClick={abrirPapelera} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="archivos-papelera-btn">
          <RotateCcw size={12} /> Papelera
        </button>
      </div>

      {/* Breadcrumb de subcarpetas */}
      <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap" data-testid="archivos-breadcrumb">
        <button onClick={() => irABreadcrumb(-1)} className="flex items-center gap-1 hover:text-[#EA580C] font-medium"><Home size={12} />{activa}</button>
        {path.map((p, idx) => (
          <span key={p.id} className="flex items-center gap-1">
            <ChevronRight size={12} />
            <button onClick={() => irABreadcrumb(idx)} className={`hover:text-[#EA580C] ${idx === path.length - 1 ? "font-bold text-gray-800" : ""}`}>{p.nombre}</button>
          </span>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex-1 flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-2xl px-4 py-4 text-sm text-gray-500 cursor-pointer justify-center hover:border-[#EA580C]">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Subir archivo aquí (máx. 12 MB, incluye PDF/imagen/DWG/Office)
          <input type="file" multiple className="hidden" onChange={e => upload(e.target.files)} data-testid="archivos-upload-input" />
        </label>
        <button onClick={() => setShowNewFolder(true)} className="flex items-center gap-1.5 px-3 py-4 rounded-2xl border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#EA580C] hover:text-[#EA580C]" data-testid="archivos-nueva-carpeta-btn">
          <FolderPlus size={16} /> Nueva carpeta
        </button>
      </div>

      {activa === "Planos" && archivosVista.length > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-400">¿El próximo archivo reemplaza a uno existente aquí?</span>
          <select value={reemplazaId} onChange={e => setReemplazaId(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid="archivos-reemplaza-select">
            <option value="">No, es un plano nuevo</option>
            {archivosVista.map(a => <option key={a.id} value={a.id}>{a.filename}</option>)}
          </select>
        </div>
      )}

      {showNewFolder && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-2" data-testid="archivos-nueva-carpeta-form">
          <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nombre de la carpeta" className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm" data-testid="archivos-nueva-carpeta-input" onKeyDown={e => e.key === "Enter" && crearCarpeta()} />
          <button onClick={crearCarpeta} className="px-3 py-1.5 rounded-lg bg-[#EA580C] text-white text-xs font-semibold" data-testid="archivos-nueva-carpeta-confirmar">Crear</button>
          <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} className="px-3 py-1.5 rounded-lg text-xs text-gray-500">Cancelar</button>
        </div>
      )}

      {carpetasHijas.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {carpetasHijas.map(f => (
            <div key={f.id} className="group relative bg-white border border-gray-200/60 rounded-xl p-3 flex items-center gap-2 cursor-pointer hover:border-[#EA580C]" onClick={() => entrarCarpeta(f)} data-testid={`archivos-subcarpeta-${f.id}`}>
              <FolderOpen size={18} className="text-[#EA580C] flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 truncate flex-1">{f.nombre}</span>
              {canFullAccess && (
                <button onClick={(e) => { e.stopPropagation(); eliminarCarpeta(f.id); }} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500" data-testid={`archivos-subcarpeta-del-${f.id}`}><Trash2 size={13} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {archivosVista.length === 0 && carpetasHijas.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm">
          <FolderOpen size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-600 font-medium">Carpeta vacía</p>
        </div>
      ) : archivosVista.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200/60 divide-y divide-gray-50">
          {archivosVista.map(a => (
            <div key={a.id} className="flex items-center justify-between px-4 py-3" data-testid={`archivos-row-${a.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon filename={a.filename} />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{a.filename}</p>
                  <p className="text-xs text-gray-400">{formatDate(a.uploaded_at)} · {(a.size / 1024).toFixed(0)} KB · {a.uploaded_by_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {activa === "Planos" && (
                  canFullAccess ? (
                    <select value={a.estado_plano || ""} onChange={e => cambiarEstadoPlano(a.id, e.target.value)} className={`text-xs font-semibold rounded-full px-2 py-0.5 border-0 ${ESTADO_BADGE[a.estado_plano] || "bg-gray-100 text-gray-500"}`} data-testid={`archivos-estado-plano-${a.id}`}>
                      {!a.estado_plano && <option value="">Sin estado</option>}
                      <option value="vigente">Vigente</option>
                      <option value="vencido">Vencido</option>
                      <option value="original">Original</option>
                    </select>
                  ) : (
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${ESTADO_BADGE[a.estado_plano] || "bg-gray-100 text-gray-500"}`}>{a.estado_plano || "—"}</span>
                  )
                )}
                <button onClick={() => download(a.id)} className="text-gray-400 hover:text-[#EA580C]" data-testid={`archivos-download-${a.id}`}><Download size={15} /></button>
                {canFullAccess && <button onClick={() => eliminar(a.id)} className="text-gray-300 hover:text-red-500" data-testid={`archivos-del-${a.id}`}><Trash2 size={15} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showPapelera && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="archivos-papelera-panel">
          <p className="text-xs font-bold text-amber-700 uppercase mb-2">Papelera</p>
          {papelera.length === 0 ? <p className="text-xs text-gray-400">Vacía</p> : papelera.map(a => (
            <div key={a.id} className="flex items-center justify-between text-xs py-1">
              <span>{a.filename} ({a.carpeta})</span>
              <button onClick={() => restaurar(a.id)} className="text-emerald-600 font-semibold hover:underline" data-testid={`archivos-restore-${a.id}`}>Restaurar</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
